import { isBedbankOffer } from 'lib/offer/offerTypes'

/**
 * Builds the `App.MetaSearchObject` describing the YMAL list anchor for a
 * meta-search property page. Returns `undefined` when the offer is missing or
 * not a bedbank offer (the only type that supports YMAL today).
 *
 * Shared between SSR (`fetchMetaSearchOffer.ts`) and client
 * (`MetaSearchPage.tsx`) so both sides compute identical filter shapes — the
 * downstream `getOfferListKey` is sensitive to field order/types and any drift
 * causes the SSR'd state to be ignored at hydration (silent re-fetch).
 */
export function buildMetaSearchObjectFromOffer(
  offer: App.AnyOffer | undefined,
  region: string,
  filters: { checkIn?: string, checkOut?: string },
  options: { limit?: number } = {},
): App.MetaSearchObject | undefined {
  if (!offer || !isBedbankOffer(offer)) return undefined
  const latitude = offer.property?.latitude
  const longitude = offer.property?.longitude
  if (latitude == null || longitude == null) return undefined

  return {
    region,
    offerCountry: offer.property?.address.countryCode ?? region,
    latitude,
    longitude,
    disabled: false,
    limit: options.limit ?? 20,
    checkIn: filters.checkIn,
    checkOut: filters.checkOut,
    offerId: offer.id,
  }
}

/**
 * Builds the `App.OfferListFilters` consumed by `fetchMetaOfferIds` /
 * `getOfferListKey`. Field set MUST stay in sync with the client-side
 * `useMetaSearchOfferList` builder so the SSR-populated `state.offer.offerLists`
 * slice is recognised on hydration.
 */
export function buildMetaSearchOfferListFilters(
  metaSearchObject: App.MetaSearchObject,
  searchDisabled = false,
): App.OfferListFilters {
  return {
    metaSearchObject: {
      region: metaSearchObject.region,
      offerCountry: metaSearchObject.offerCountry,
      latitude: metaSearchObject.latitude,
      longitude: metaSearchObject.longitude,
      limit: metaSearchObject.limit ?? 20,
      disabled: searchDisabled,
      offerId: metaSearchObject.offerId,
    },
  }
}
